/**
 * A5 Contents rail: sticky section list beside the full label (R6.1-R6.5).
 * Presentational; the page owns entries, counts, scroll-spy and Matches-only.
 */
import React, { useEffect, useState } from 'react';

export interface RailEntry {
  key: string;
  title: string;
  level: 1 | 2;
  sectionNumber: string | null;
  absent?: boolean | undefined;
  boxedWarning?: boolean | undefined;
  highlights?: boolean | undefined;
}

const LG_QUERY = '(min-width: 1024px)';

/** True at >= lg; true when matchMedia is missing (jsdom). Tracks `change`. */
export function useMinWidthLg(): boolean {
  const get = () => (typeof window.matchMedia === 'function' ? window.matchMedia(LG_QUERY).matches : true);
  const [isLg, setIsLg] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(LG_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsLg(e.matches);
    setIsLg(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isLg;
}

interface LabelContentsRailProps {
  entries: RailEntry[];
  /** null = no badges (D12). */
  counts: Map<string, number> | null;
  /** null = all rows (Full label); else the Matches-only keep set (R6.5). */
  visibleKeys: Set<string> | null;
  activeKey: string | null;
  onSelect: (e: RailEntry) => void;
  style?: React.CSSProperties | undefined;
}

const ABSENT_TIP = 'Not present in the official PMDA package insert for this drug';

const LabelContentsRail: React.FC<LabelContentsRailProps> = ({
  entries, counts, visibleKeys, activeKey, onSelect, style,
}) => (
  <nav
    aria-label="Label sections"
    className="w-[236px] shrink-0 self-start sticky overflow-y-auto"
    style={style}
  >
    <span className="eyebrow">Contents</span>
    <ul className="mt-2 space-y-0.5">
      {entries.filter((e) => !visibleKeys || visibleKeys.has(e.key)).map((e) => {
        const pad = e.level === 2 ? 'pl-6' : 'pl-2';
        const base = `block w-full text-left rounded py-1 pr-2 text-sm ${pad}`;
        if (e.absent) {
          return (
            <li key={e.key}>
              <div className={`${base} text-ink-3/50`} title={ABSENT_TIP}>{e.title}</div>
            </li>
          );
        }
        const n = counts?.get(e.key) ?? 0;
        const active = e.key === activeKey;
        const tone = active
          ? 'bg-accent-tint text-accent-deep'
          : e.boxedWarning ? 'text-signal font-medium hover:bg-accent-tint' : 'text-ink-2 hover:bg-accent-tint';
        return (
          <li key={e.key}>
            <button
              type="button"
              className={`${base} flex items-baseline justify-between gap-2 ${tone}`}
              aria-current={active ? 'location' : undefined}
              onClick={() => onSelect(e)}
            >
              <span>{e.title}</span>
              {counts && n >= 1 && <span className="font-mono text-[11px]">{n}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  </nav>
);

export default LabelContentsRail;
